/** public/index.html adds this class on the homepage tab URLs, where the static shell is shown. */
const HOME_SHELL_CLASS = 'yovo-home-shell';
const SHELL_HERO_SELECTOR = '#yovo-lcp-shell .yovo-mobile-header-hero';

/** Longest React waits for the shell's hero image. On a slow network main.js arrives later anyway. */
const HERO_WAIT_MS = 300;

/** Background tabs never fire requestAnimationFrame; start anyway after this long. */
const START_FALLBACK_MS = 100;

/**
 * Resolves once the shell hero's background image is decoded, so the next frame paints it.
 * Resolves at once on desktop (hero hidden) or when there is no image to wait for.
 */
function shellHeroReady(doc, win) {
  const hero = doc.querySelector(SHELL_HERO_SELECTOR);
  if (!hero || !hero.getBoundingClientRect().height || typeof win.Image !== 'function') return Promise.resolve();
  const match = /url\(["']?([^"')]+)["']?\)/.exec(win.getComputedStyle(hero).backgroundImage || '');
  if (!match) return Promise.resolve();
  const image = new win.Image();
  image.src = match[1];
  return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
}

/**
 * On the homepage, run `start` (React's first render) only after the browser has painted the
 * static shell from index.html, hero image included. When main.js is cached or arrives quickly,
 * React can otherwise render first: the shell's hero never paints, and React's copy of it becomes
 * the LCP element. Every other page starts right away.
 *
 * @param {() => void} start
 * @param {{ doc?: Document, win?: Window }} [env]
 */
export function startAfterFirstPaint(start, { doc = document, win = window } = {}) {
  if (!doc.documentElement.classList.contains(HOME_SHELL_CLASS) || typeof win.requestAnimationFrame !== 'function') {
    start();
    return;
  }
  let started = false;
  const once = () => {
    if (started) return;
    started = true;
    start();
  };
  const heroWait = new Promise((resolve) => {
    win.setTimeout(resolve, HERO_WAIT_MS);
    shellHeroReady(doc, win).then(resolve);
  });
  heroWait.then(() => {
    // rAF runs just before the next paint; a 0 ms timeout from there runs just after it.
    win.requestAnimationFrame(() => win.setTimeout(once, 0));
    win.setTimeout(once, START_FALLBACK_MS);
  });
}
